import { useContext, useEffect, useState } from "react"
import { createDrillingDataEntry } from "../../../../services/api"
import { AuthContext } from "../../../../context/AuthContext";

const DrillingDataEntryForm = () => {

  // Get vehicles data and functions from context
  const {
    getAllVehicles,
    allVehicles,
    allVehiclesLoading,
    allVehiclesError
  } = useContext(AuthContext);

  // Get today's date
  const today = new Date().toISOString().split('T')[0];  // This gives the date in YYYY-MM-DD format

  const [formData, setFormData] = useState({
    date: today,  // Set initial date to today's date
    vehicleName: "",
    typeOfCustomer: "direct",
    name: "",
    address: "",
    mobileNumber: "",
    drillFeet: 0,
    drillRate: 0,
    feetTotal: 0,
    additional: [{ name: "", quantity: 0, rate: 0, total: 0 }]
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch vehicles on component mount
  useEffect(() => {
    if (!allVehicles || allVehicles.length === 0) {
      getAllVehicles();
    }
  }, [allVehicles]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    const updatedFormData = {
      ...formData,
      [name]: value,
    };

    // Auto calculate feetTotal
    if (name === "drillFeet" || name === "drillRate") {
      const drillFeet =
        name === "drillFeet" ? Number(value) : Number(updatedFormData.drillFeet);
      const drillRate =
        name === "drillRate" ? Number(value) : Number(updatedFormData.drillRate);

      updatedFormData.feetTotal = drillFeet * drillRate;
    }

    setFormData(updatedFormData);

    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };


  const handleAdditionalChange = (index, field, value) => {
    const updatedAdditional = [...formData.additional];
    updatedAdditional[index][field] = value;
    if (field === "quantity" || field === "rate") {
      updatedAdditional[index].total = updatedAdditional[index].quantity * updatedAdditional[index].rate;
    }
    setFormData({ ...formData, additional: updatedAdditional });
  };

  const addAdditionalField = () => {
    setFormData({
      ...formData,
      additional: [...formData.additional, { name: "", quantity: 0, rate: 0, total: 0 }]
    });
  };

  const removeAdditionalField = (index) => {
    if (formData.additional.length > 1) {
      const updatedAdditional = formData.additional.filter((_, i) => i !== index);
      setFormData({ ...formData, additional: updatedAdditional });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.date.trim()) {
      newErrors.date = "Date is required";
    }

    if (!formData.vehicleName.trim()) {
      newErrors.vehicleName = "Vehicle name is required";
    }

    if (formData.mobileNumber && !/^\d{10}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = "Mobile number must be 10 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Filter out empty additional items
      const filteredAdditional = formData.additional.filter(
        item => item.name.trim() || item.quantity > 0 || item.rate > 0
      );

      const submitData = {
        ...formData,
        additional: filteredAdditional
      };

      const response = await createDrillingDataEntry(submitData);
      console.log(response);

      if (response.success) {
        alert("Drilling data entry saved successfully!");
        // Reset form
        setFormData({
          date: today,
          vehicleName: "",
          typeOfCustomer: "direct",
          name: "",
          address: "",
          mobileNumber: "",
          drillFeet: 0,
          drillRate: 0,
          feetTotal: 0,
          additional: [{ name: "", quantity: 0, rate: 0, total: 0 }]
        });
        setErrors({});
      }
    } catch (error) {
      console.error("Error saving drilling data:", error);
      alert(error || "Failed to save drilling data entry");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 border border-gray-200 shadow-lg rounded-xl">
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-6 w-6 text-gray-700"
          >
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5" />
            <path d="M2 12l10 5 10-5" />
          </svg>
          <h2 className="text-2xl font-semibold text-gray-800">Drilling Data Entry Form</h2>
        </div>
        <p className="text-gray-600 mt-1">Register and manage drilling operation data</p>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="space-y-2">
            <label htmlFor="date" className="text-sm font-medium text-gray-700">
              Date *
            </label>
            <input
              id="date"
              name="date"
              type="date"
              value={formData.date}
              onChange={handleChange}
              required
              className={`w-full p-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 ${errors.date ? 'border-red-500' : 'border-gray-300'}`}
            />
            {errors.date && <p className="text-red-500 text-xs">{errors.date}</p>}
          </div>

          <div className="space-y-2">
            <label htmlFor="vehicleName" className="text-sm font-medium text-gray-700">
              Vehicle Name *
            </label>
            <select
              id="vehicleName"
              name="vehicleName"
              value={formData.vehicleName}
              onChange={handleChange}
              required
              disabled={allVehiclesLoading}
              className={`w-full p-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 ${errors.vehicleName ? 'border-red-500' : 'border-gray-300'} ${allVehiclesLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <option value="">
                {allVehiclesLoading ? 'Loading vehicles...' : 'Select a vehicle'}
              </option>
              {allVehicles && allVehicles.length > 0 && allVehicles.map((vehicle) => (
                <option key={vehicle._id} value={vehicle.vehicleName}>
                  {vehicle.vehicleName} ({vehicle.vehicleNumber})
                </option>
              ))}
            </select>
            {errors.vehicleName && <p className="text-red-500 text-xs">{errors.vehicleName}</p>}
            {allVehiclesError && (
              <p className="text-red-500 text-xs">Error loading vehicles. Please try again.</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="name" className="text-sm font-medium text-gray-700">
              Customer Name
            </label>
            <input
              id="name"
              name="name"
              placeholder="Enter customer name"
              value={formData.name}
              onChange={handleChange}
              className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="address" className="text-sm font-medium text-gray-700">
              Address
            </label>
            <input
              id="address"
              name="address"
              placeholder="Enter customer address"
              value={formData.address}
              onChange={handleChange}
              className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="mobileNumber" className="text-sm font-medium text-gray-700">
              Mobile Number
            </label>
            <input
              id="mobileNumber"
              name="mobileNumber"
              type="tel"
              placeholder="Enter mobile number"
              value={formData.mobileNumber}
              onChange={handleChange}
              className={`w-full p-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 ${errors.mobileNumber ? 'border-red-500' : 'border-gray-300'}`}
            />
            {errors.mobileNumber && <p className="text-red-500 text-xs">{errors.mobileNumber}</p>}
          </div>

          <div className="space-y-2">
            <label htmlFor="drillFeet" className="text-sm font-medium text-gray-700">
              Drill Feet
            </label>
            <input
              id="drillFeet"
              name="drillFeet"
              type="number"
              placeholder="Enter drill feet"
              value={formData.drillFeet}
              onChange={handleChange}
              min="0"
              className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400"
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="drillRate" className="text-sm font-medium text-gray-700">
              Feet Rate
            </label>
            <input
              id="drillRate"
              name="drillRate"
              type="number"
              min="0"
              placeholder="Enter feet rate"
              value={formData.drillRate}
              onChange={handleChange}
              className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="feetTotal" className="text-sm font-medium text-gray-700">
              Feet Total
            </label>
            <input
              id="feetTotal"
              name="feetTotal"
              type="number"
              value={formData.feetTotal}
              disabled
              className="w-full p-2.5 border border-gray-300 rounded-md bg-gray-100 focus:outline-none"
            />
          </div>

        </div>

        {/* Additional Items Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-700">Additional Items</h3>
            <button
              type="button"
              onClick={addAdditionalField}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors duration-200 text-sm"
            >
              Add Item
            </button>
          </div>

          {formData.additional.map((item, index) => (
            <div key={index} className="grid grid-cols-4 md:grid-cols-4 gap-4 p-4 border border-gray-200 rounded-md bg-gray-50">
              <div className="space-y-2">

                <label className="text-sm font-medium text-gray-700">
                  Item Name
                </label>
                <input
                  placeholder="Enter item name"
                  value={item.name}
                  onChange={(e) => handleAdditionalChange(index, 'name', e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">
                    Quantity
                  </label>

                </div>
                <input
                  placeholder="Enter quantity"
                  value={item.quantity}
                  onChange={(e) => handleAdditionalChange(index, 'quantity', e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Rate
                </label>
                <input
                  placeholder="Enter rate"
                  value={item.rate}
                  onChange={(e) => handleAdditionalChange(index, 'rate', e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400"
                />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <label className="text-sm font-medium text-gray-700">
                    Total
                  </label>
                  {formData.additional.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeAdditionalField(index)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <input
                  placeholder="Total"
                  value={item.total}
                  disabled
                  className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400"
                />
              </div>

            </div>
          ))}
        </div>

        <div className="pt-4">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className={`w-full py-3 rounded-md transition-colors duration-200 font-medium ${loading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-gray-800 hover:bg-gray-700'
              } text-white`}
          >
            {loading ? 'Saving...' : 'Save Drilling Data'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DrillingDataEntryForm;
